import type { SqlImportWriteInput } from '@/services/db/sql-import/mutations';
import type { SqlImport } from '@/services/db/sql-import/entity';
import { ImportType } from '@/services/db/sql-import/entity';
import type { SqlDatabaseType } from '@/services/db/sql-import-database/entity';

/**
 * The import the wizard is building: the write body, kept in the wire's shape so
 * each step edits the fields it will send. `database_type` is fixed by the route
 * and added at save time.
 */
export type SqlImportDraft = Omit<SqlImportWriteInput, 'database_type' | 'import_status'>;

/** The identifier an object import matches rows on. */
const OBJECT_ID_FIELD = 'object_id';
/** How often a new import syncs until the Settings step says otherwise. */
export const DEFAULT_INTERVAL_SECONDS = 3600;
/** The identifier a people import matches rows on when the workspace cannot use email. */
const PERSON_ID_FIELD = 'id';

/**
 * A new import before any step ran, with the defaults the Settings step shows, so a save before
 * that step carries them. A workspace that cannot identify people by email has only one choice
 * for the id field, so it is set up front.
 */
export function newSqlImportDraft(canIdentifyByEmail: boolean): SqlImportDraft {
  return {
    name: '',
    description: '',
    import_type: ImportType.workspace,
    id_field_name: canIdentifyByEmail ? undefined : PERSON_ID_FIELD,
    interval: DEFAULT_INTERVAL_SECONDS,
    start_at: 0,
    query: '',
    location: '',
    database_id: undefined,
    segment_id: undefined,
    segment_name: undefined,
    object_type_id: undefined,
    clear_empty_cells: false,
    ignore_suppress_err: true,
    relationship: undefined,
  };
}

/**
 * A saved import reopened in the wizard. A draft that was saved before its id
 * field was chosen gets the default for its import type, so the settings step has
 * a value to show.
 */
export function sqlImportToDraft(sqlImport: SqlImport): SqlImportDraft {
  // The wire types the field as a string; an unknown value reads as a people import.
  const importType = Object.values(ImportType).find((value) => value === sqlImport.import_type) ?? ImportType.workspace;
  const idFieldName =
    sqlImport.id_field_name === ''
      ? importType === ImportType.object
        ? OBJECT_ID_FIELD
        : PERSON_ID_FIELD
      : sqlImport.id_field_name;

  return {
    name: sqlImport.name,
    description: sqlImport.description,
    import_type: importType,
    id_field_name: idFieldName,
    interval: sqlImport.interval === 0 ? DEFAULT_INTERVAL_SECONDS : sqlImport.interval,
    start_at: sqlImport.start_at,
    query: sqlImport.query,
    location: sqlImport.location,
    database_id: sqlImport.database_id,
    segment_id: sqlImport.segment_id ?? undefined,
    segment_name: sqlImport.segment_name ?? undefined,
    object_type_id: sqlImport.object_type_id ?? undefined,
    clear_empty_cells: sqlImport.clear_empty_cells,
    ignore_suppress_err: sqlImport.ignore_suppress_err,
    relationship:
      sqlImport.relationship == null
        ? undefined
        : {
            query: sqlImport.relationship.query,
            entity1_type_id: sqlImport.relationship.entity1_type_id,
            entity1_id_field_name: sqlImport.relationship.entity1_id_field_name,
            entity2_type_id: sqlImport.relationship.entity2_type_id,
            entity2_id_field_name: sqlImport.relationship.entity2_id_field_name,
          },
  };
}

/** Whether two drafts would send the same body: same fields set, to the same values, in any order. */
export function sqlImportDraftEquals(a: SqlImportDraft, b: SqlImportDraft): boolean {
  return JSON.stringify(setEntries(a)) === JSON.stringify(setEntries(b));
}

function setEntries(draft: SqlImportDraft) {
  return Object.entries(draft)
    .filter(([, value]) => value !== undefined)
    .sort(([a], [b]) => a.localeCompare(b));
}

export type { SqlDatabaseType };

/**
 * The `?id=` a wizard URL resumes: a positive integer, as a number or a digit string. The router
 * hands over a number for `?id=7`; the embedded Ember app's links carry the same unquoted form.
 */
export function parseDraftIdParam(value: unknown): number | undefined {
  const candidate = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value;
  return typeof candidate === 'number' && Number.isSafeInteger(candidate) && candidate > 0 ? candidate : undefined;
}
